import { IDropdownOption } from '@fluentui/react';
import React from 'react';
import { Route, Routes, useSearchParams } from 'react-router-dom';
import BasicBlockUI from './components/Common/BasicBlockUI';
import Base from './components/Common/Layout/Base';
import { NotFound } from './components/NotFound';
import ResumenTorneos from './components/Torneo/ResumenTorneos';
import TorneoDetailView from './components/Torneo/TorneoView';
import Environment from './lib/Environment';
import MessageCourier from './lib/MessageCourier';
import XhrErrorListener from './lib/XhrErrorListener';
import XhrRequestError from './lib/XhrRequestError';
import SystemClient, { Club, ClubContext, ContextMenu, Federacion, Rama } from './system/SystemClient';

export interface AppContext {
  system: SystemClient;
  courier: MessageCourier;
  changeBlockUI: (v: boolean) => void;
  noAdvertising: string | null;
}

interface IRouterContextProps {
  system: SystemClient;
  environment: Environment;
  courier: MessageCourier;
}

class ErrorListener implements XhrErrorListener {
  constructor(private onAuthenticationError: (error: XhrRequestError) => void) {}

  receiveError(error: XhrRequestError): void {
    console.log('error :>> **', error);
    if (error.isAuthenticationRequiredError()) {
      this.onAuthenticationError(error);
    }
  }
}

export interface MenuFilters {
  temporadaId: string;
  federacionId: string;
  categoriaId: string;
  rama: string;
}

export const calcularTotalTorneosPorFederacion = (federacion: Federacion) => {
  return federacion.ramas.reduce((total, rama) => {
    return (
      total +
      rama.categorias.reduce((subtotal, categoria) => {
        return subtotal + +categoria.cantTorneos;
      }, 0)
    );
  }, 0);
};

export const calcularTotalTorneosPorRama = (rama: Rama) => {
  return rama.categorias.reduce((total, categoria) => {
    return total + parseInt(categoria.cantTorneos, 10);
  }, 0);
};

const getLegendCantTorneos = (cantTorneos: number) =>
  cantTorneos === 0 ? '(Sin torneos)' : `(${cantTorneos})`;

export const CRITERIO_TEMPORADA = 'T';
export const CRITERIO_FEDERACION = 'F';
export const CRITERIO_CATEGORIA = 'C';
export const CRITERIO_RAMA = 'R';

/* const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
const MountFadeSlide: React.FC<{ in: boolean; children: React.ReactNode }> = ({ in: show, children }) => {
  const [mounted, setMounted] = React.useState(show);
  const [visible, setVisible] = React.useState(show);

  React.useEffect(() => {
    if (show) { setMounted(true); const t = setTimeout(() => setVisible(true), 10); return () => clearTimeout(t); }
    setVisible(false); const t = setTimeout(() => setMounted(false), 420); return () => clearTimeout(t);
  }, [show]);

  if (!mounted) return null;
  return (
    <div style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0) scale(1)' : 'translateY(6px) scale(0.985)',
      transition: `opacity 300ms ease, transform 420ms ${EASE}`,
      willChange: 'opacity, transform',
    }}>
      {children}
    </div>
  );
};
 */

const AppInRouterContext = (props: IRouterContextProps) => {
  const [block, setBlock] = React.useState(false);

  const changeBlockUI = (shouldBlock: boolean) => {
    setBlock(shouldBlock);
  };

  const [searchParams] = useSearchParams();
  const noAdvertising = searchParams.get('noAdv'); // ejemplo: ?myParam=valor
  const clubIdParam = searchParams.get('clubId'); // ejemplo: ?myParam=valor

  const appContext: AppContext = {
    system: props.system,
    changeBlockUI: changeBlockUI,
    courier: props.courier,
    noAdvertising: noAdvertising,
  };

  const [contextMenu, setContextMenu] = React.useState<ContextMenu[] | undefined>(undefined);
  const [clubes, setClubes] = React.useState<Club[]>([]);
  const [categorias, setCategorias] = React.useState<IDropdownOption<any>[]>([]);
  const [temporadas, setTemporadas] = React.useState<IDropdownOption<any>[]>([]);
  const [federaciones, setFederaciones] = React.useState<IDropdownOption<any>[]>([]);
  const [ramas, setRamas] = React.useState<IDropdownOption<any>[]>([]);

  const setOptionsByTemporada = (temporada: ContextMenu) => {
    setMenuFilters({ ...menuFilters, temporadaId: temporada.id });
    const conTorneos = temporada.federaciones.filter((fed) => calcularTotalTorneosPorFederacion(fed) !== 0); // Filtra todos los que no son 0
    const sinTorneos = temporada.federaciones.filter((fed) => calcularTotalTorneosPorFederacion(fed) === 0); // Filtra solo los ceros
    const federaciones = conTorneos.concat(sinTorneos); // Combina ambos arrays, primero los que no son 0, luego los ceros

    setFederaciones(
      federaciones.map((f) => {
        const cantTorneos = calcularTotalTorneosPorFederacion(f);
        return {
          key: f.id,
          text: `${f.nombre} `,
          data: { cantTorneos: +cantTorneos },
          disabled: +cantTorneos === 0,
          title: +cantTorneos === 0 ? 'Sin torneos' : '',
        };
      })
    );
  };

  React.useEffect(() => {
    const getContext = async () => {
      try {
        changeBlockUI(true);
        const ret = await props.system.getContextMenu();
        setContextMenu(ret);
        const temporadaActual = ret.find((t) => t.actual);

        setTemporadas(
          ret.map((t) => {
            return { key: t.id, text: t.descripcion };
          })
        );

        if (temporadaActual) {
          setOptionsByTemporada(temporadaActual);
        }
      } catch (error) {
        console.log(error);
      } finally {
        changeBlockUI(false);
      }
    };

    const getClubes = async () => {
      try {
        changeBlockUI(true);
        const ret = await props.system.getClubes();
        const clubTodos: Club = { club: ' Todos', clubId: '0', clubEscudo: '' };
        ret.push(clubTodos);
        setClubes(ret);
      } catch (error) {
        console.log(error);
      } finally {
        changeBlockUI(false);
      }
    };

    getContext();
    getClubes();
  }, [props.system]);

  const [menuFilters, setMenuFilters] = React.useState<MenuFilters>({
    temporadaId: '',
    federacionId: props.environment.federacionDefault() ? props.environment.federacionDefault() : '',
    categoriaId: '',
    rama: 'F',
  });

  const resetMenuFilters = () =>
    setMenuFilters({
      temporadaId: '',
      federacionId: props.environment.federacionDefault() ? props.environment.federacionDefault() : '',
      categoriaId: '',
      rama: '',
    });

  const [clubId, setClubId] = React.useState<string>('');
  const [clubContext, setClubContext] = React.useState<ClubContext | undefined>(undefined);

  React.useEffect(() => {
    if (clubIdParam) {
      setClubId(clubIdParam);
    } else {
      setClubId('0');
    }
  }, [clubIdParam]);

  React.useEffect(() => {
    const n = Number(clubId);

    const getClubContext = async () => {
      if (!Number.isFinite(n) || n < 0) {
        setClubContext(undefined);
        return;
      }

      if (!!!clubId) return;
      try {
        changeBlockUI(true);
        const ret = await props.system.getClub(clubId);
        console.log('ret club', ret);

        setClubContext(ret);
      } catch (error) {
        console.log(error);
      } finally {
        changeBlockUI(false);
      }
    };
    getClubContext();
  }, [clubId]);

  React.useEffect(() => {
    if (menuFilters.federacionId) {
      const fed = contextMenu
        ?.find((c) => c.id == menuFilters.temporadaId)
        ?.federaciones.find((f) => f.id === menuFilters.federacionId);
      if (fed) {
        const conTorneos = fed.ramas.filter((ramas) => calcularTotalTorneosPorRama(ramas) !== 0); // Filtra todos los que no son 0
        const sinTorneos = fed.ramas.filter((ramas) => calcularTotalTorneosPorRama(ramas) === 0); // Filtra solo los ceros
        const ramas = conTorneos.concat(sinTorneos); // Combina ambos arrays, primero los que no son 0, luego los ceros

        setRamas(
          ramas.map((r) => {
            const cantTorneos = calcularTotalTorneosPorRama(r);
            return {
              key: r.key,
              text: `${r.text} `,
              data: { cantTorneos: +cantTorneos },
              disabled: +cantTorneos === 0,
              title: +cantTorneos === 0 ? 'Sin torneos' : '',
            };
          })
        );
      }
      const rama = contextMenu
        ?.find((c) => c.id == menuFilters.temporadaId)
        ?.federaciones.find((f) => f.id === menuFilters.federacionId)
        ?.ramas.find((r) => r.key === menuFilters.rama);
      if (rama) {
        const conTorneos = rama.categorias.filter((cat) => +cat.cantTorneos !== 0); // Filtra todos los que no son 0
        const sinTorneos = rama.categorias.filter((cat) => +cat.cantTorneos === 0); // Filtra solo los ceros
        const categorias = conTorneos.concat(sinTorneos); // Combina ambos arrays, primero los que no son 0, luego los ceros

        setCategorias(
          categorias.map((c) => {
            return {
              key: c.id,
              text: `${c.nombre} ${getLegendCantTorneos(+c.cantTorneos)}`,
              data: { cantTorneos: +c.cantTorneos },
              disabled: +c.cantTorneos === 0,
              title: +c.cantTorneos === 0 ? 'Sin torneos' : '',
            };
          })
        );
      }
    }
  }, [menuFilters]);

  const onChangeFilters = (criterio: string, value: string) => {
    switch (criterio) {
      case CRITERIO_TEMPORADA:
        if (value !== menuFilters.temporadaId) {
          const t = contextMenu?.find((t) => t.id === value);
          if (t) setOptionsByTemporada(t);
          setMenuFilters({ temporadaId: value, federacionId: props.environment.federacionDefault(), categoriaId: '', rama: 'F' });
        }
        break;
      case CRITERIO_FEDERACION:
        if (value !== menuFilters.federacionId) {
          setMenuFilters({ ...menuFilters, federacionId: value });
        }
        break;
      case CRITERIO_CATEGORIA:
        if (value !== menuFilters.categoriaId) {
          setMenuFilters({ ...menuFilters, categoriaId: value });
        }
        break;
      case CRITERIO_RAMA:
        if (value !== menuFilters.rama) {
          setMenuFilters({ ...menuFilters, rama: value });
        }
        break;
    }
  };

  const showTorneos = !!(menuFilters.temporadaId && menuFilters.federacionId && menuFilters.rama);

  return (
    <>
      <BasicBlockUI blockUI={block} />
      <Routes>
        <Route path="/" element={<Base appContext={appContext} />}>
          <Route
            path=":id"
            element={
              <TorneoDetailView
                categorias={categorias}
                temporadas={temporadas}
                federaciones={federaciones}
                ramas={ramas}
                onChangeFilters={onChangeFilters}
                menuFilters={menuFilters}
                appContext={appContext}
              />
            }
          />
          {/* <Route
            path=":id/:clubId"
            element={
              <ResumenTorneos
                categorias={categorias}
                temporadas={temporadas}
                federaciones={federaciones}
                ramas={ramas}
                onChangeFilters={onChangeFilters}
                appContext={appContext}
                menuFilters={menuFilters}
              />
            }
          /> */}
          <Route
            path=""
            element={
              // <MountFadeSlide in={showTorneos}>
              <ResumenTorneos
                categorias={categorias}
                temporadas={temporadas}
                federaciones={federaciones}
                ramas={ramas}
                onChangeFilters={onChangeFilters}
                appContext={appContext}
                menuFilters={menuFilters}
                clubes={clubes}
                clubId={clubId}
                setClubId={(v) => {
                  setClubId(v);
                  setMenuFilters({ ...menuFilters, rama: '', categoriaId: '' });
                }}
                clubContext={clubContext}
              />
            }
          />

          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </>
  );
};

export default AppInRouterContext;
